import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, Environment, Sparkles, MeshDistortMaterial, Stars } from '@react-three/drei';
import * as THREE from 'three';

function TorusKnot() {
  const ref = useRef();
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (ref.current) {
      ref.current.rotation.x = t * 0.15;
      ref.current.rotation.y = t * 0.2;
    }
  });
  return (
    <Float speed={1.4} rotationIntensity={0.7} floatIntensity={1.2}>
      <mesh ref={ref} position={[2.2, 0.2, 0]} scale={1.15}>
        <torusKnotGeometry args={[0.9, 0.26, 220, 40]} />
        <MeshDistortMaterial
          color="#d5b56b"
          metalness={0.9}
          roughness={0.15}
          distort={0.28}
          speed={1.6}
        />
      </mesh>
    </Float>
  );
}

function Sphere() {
  const ref = useRef();
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (ref.current) {
      // Anchored at the bottom-left of the hero, gentle drift only.
      // Vertical range stays within [-2.6, -2.2] — well below the h1 headline.
      ref.current.position.y = -2.4 + Math.sin(t * 0.5) * 0.15;
      ref.current.position.x = -4.6 + Math.cos(t * 0.35) * 0.12;
    }
  });
  return (
    <Float speed={0.6} floatIntensity={0.3} rotationIntensity={0.4}>
      <mesh ref={ref} position={[-4.6, -2.4, -1]} scale={0.7}>
        <icosahedronGeometry args={[1, 4]} />
        <MeshDistortMaterial
          color="#1a2456"
          metalness={0.6}
          roughness={0.3}
          distort={0.5}
          speed={2}
          emissive="#0a1230"
          emissiveIntensity={0.5}
        />
      </mesh>
    </Float>
  );
}

function Ring() {
  const ref = useRef();
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (ref.current) {
      // Slow z-axis rotation only. No axial tilt — that was causing the ring's
      // top/bottom edge to sweep across the headline as a thin gold "wire".
      ref.current.rotation.z = t * 0.15;
    }
  });
  return (
    <mesh ref={ref} position={[0, 0, -3]} scale={2.05}>
      <torusGeometry args={[1, 0.02, 16, 200]} />
      <meshStandardMaterial color="#d5b56b" metalness={1} roughness={0.2} emissive="#8a6a2a" emissiveIntensity={0.4} />
    </mesh>
  );
}

function ParticleField() {
  const ref = useRef();
  const count = 400;
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 8;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    return arr;
  }, []);
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.getElapsedTime() * 0.03;
    }
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={count}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.02}
        color="#d5b56b"
        transparent
        opacity={0.7}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

export default function HeroCanvas() {
  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: [0, 0, 5], fov: 55 }}
      gl={{ antialias: true, alpha: true }}
    >
      <color attach="background" args={[0, 0, 0, 0]} />
      <ambientLight intensity={0.35} />
      <directionalLight position={[5, 5, 5]} intensity={1.1} color="#f0d78c" />
      <directionalLight position={[-5, -3, -2]} intensity={0.6} color="#4a6bc9" />
      <pointLight position={[0, 0, 3]} intensity={0.6} color="#d5b56b" />

      <TorusKnot />
      <Sphere />
      <Ring />
      <ParticleField />

      <Stars radius={40} depth={40} count={800} factor={2} saturation={0} fade speed={0.8} />
      <Sparkles count={40} scale={8} size={2} speed={0.4} color="#d5b56b" />

      <Environment preset="night" />
    </Canvas>
  );
}
